// Detect Claude Code CLI ("claude" binary) trên PATH + parse SemVer version.
// Trả structured info để các module sau (install / auth-check) decide flow.
// Cross-platform: Unix dùng `which`, Windows dùng `where`.
// Lưu ý: TRÁNH `command -v` (shell builtin) + `shell:true` — Node ≥23 emit DEP0190.
import { spawnSync } from "node:child_process";
import { detectHostPlatform } from "./detect-host-platform.js";

export interface ClaudeCodeInstallationInfo {
  installed: boolean;
  version: string | null; // SemVer string khi detect được, vd "1.2.3"
  path: string | null; // Absolute path đến binary nếu tìm thấy
}

// Timeout cho `claude --version` — nếu binary hang thì abort tránh block init.
const VERSION_PROBE_TIMEOUT_MS = 5000;

// Regex SemVer chấp nhận extra suffix vd "1.2.3 (Claude Code)" hoặc "claude 1.2.3".
const SEMVER_REGEX = /(\d+\.\d+\.\d+)/;

// Probe binary location qua `which` (POSIX) hoặc `where` (Windows).
// Cả 2 đều là external executable → KHÔNG cần shell:true → tránh DEP0190 trên Node 23+.
function probeClaudeBinaryPath(): string | null {
  const isWindows = detectHostPlatform() === "win32";
  const probeCmd = isWindows ? "where" : "which";
  const result = spawnSync(probeCmd, ["claude"], { encoding: "utf8" });

  if (result.error || result.status !== 0) return null;
  const out = (result.stdout || "").trim();
  if (!out) return null;
  // `where` có thể list nhiều dòng — lấy dòng đầu.
  // out đã verified non-empty (line 29) → split[0] luôn tồn tại.
  return out.split(/\r?\n/)[0]!.trim();
}

// Parse `claude --version` output → SemVer. Null nếu không match.
function probeClaudeVersion(): string | null {
  const result = spawnSync("claude", ["--version"], {
    encoding: "utf8",
    timeout: VERSION_PROBE_TIMEOUT_MS,
  });

  if (result.error || result.status !== 0) return null;
  const out = (result.stdout || "").trim();
  const match = SEMVER_REGEX.exec(out);
  return match?.[1] ?? null;
}

// Main API: detect Claude Code installation state.
// Không throw — luôn return structured info để caller branch logic.
//
// v1.13.0 perf: Module-level cache để tránh spawnSync `which claude` + `claude --version`
// mỗi lần caller gọi. Init flow gọi function này 3+ lần → save 1-3s. Caller có thể
// invalidate qua `invalidateClaudeCodeInstallationCache()` sau khi install/uninstall.
let cachedInfo: ClaudeCodeInstallationInfo | null = null;

export function detectClaudeCodeInstallation(): ClaudeCodeInstallationInfo {
  if (cachedInfo !== null) return cachedInfo;
  const path = probeClaudeBinaryPath();
  if (!path) {
    cachedInfo = { installed: false, version: null, path: null };
    return cachedInfo;
  }
  const version = probeClaudeVersion();
  // installed = path tồn tại VÀ version parse được → binary thực sự dùng được.
  // Nếu version=null (binary hỏng / output rác) → installed=false để caller (run-ai-setup)
  // cài lại qua npm thay vì im lặng dùng binary lỗi. (N2 bug fix)
  cachedInfo = { installed: !!(path && version), version, path };
  return cachedInfo;
}

// Caller gọi sau khi install/uninstall để re-probe state.
export function invalidateClaudeCodeInstallationCache(): void {
  cachedInfo = null;
}
